import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Button } from './Button';

/**
 * Modálny dialóg s animáciou a možnosťou prispôsobenia
 * 
 * @param {Object} props - Vlastnosti komponentu
 * @param {boolean} props.isOpen - Či je modálne okno otvorené
 * @param {Function} props.onClose - Funkcia volaná pri zatvorení
 * @param {string} props.title - Nadpis modálneho okna
 * @param {React.ReactNode} props.children - Obsah modálneho okna
 * @param {string} props.cancelText - Text tlačidla pre zrušenie akcie (voliteľné)
 * @param {string} props.confirmText - Text tlačidla pre potvrdenie akcie (voliteľné)
 * @param {Function} props.onConfirm - Funkcia volaná pri potvrdení
 * @param {string} props.variant - Variant modálneho okna (default, warning, success)
 */
const Modal = ({ 
  isOpen, 
  onClose, 
  title, 
  children, 
  cancelText = "Zrušiť", 
  confirmText = "Potvrdiť", 
  onConfirm,
  variant = "default"
}) => {
  // Zastavenie scrollovania pozadia, keď je modálne okno otvorené
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  // Zatvoriť modálne okno pri stlačení ESC
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  // Animácie
  const backdropVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1 }
  };
  
  const modalVariants = {
    hidden: { y: -20, opacity: 0 },
    visible: { y: 0, opacity: 1, transition: { delay: 0.1, type: "spring", stiffness: 400, damping: 30 } }
  };

  // Mapovanie variantov na farby
  const variantStyles = {
    default: "border-accent text-primary",
    warning: "border-red-500 text-red-600",
    success: "border-green-500 text-green-600"
  };

  // Zabrániť rozšíreniu kliku na pozadie
  const handleModalClick = (e) => {
    e.stopPropagation();
  };

  if (!isOpen) return null;

  return (
    <motion.div
      className="fixed inset-0 bg-black/50 flex justify-center items-center p-4 z-50"
      initial="hidden"
      animate="visible"
      exit="hidden"
      variants={backdropVariants}
      onClick={onClose}
    >
      <motion.div 
        className={`bg-white rounded-lg shadow-xl w-full max-w-md border-t-4 ${variantStyles[variant]}`}
        variants={modalVariants}
        onClick={handleModalClick}
      >
        <div className="p-5 border-b">
          <h3 className="text-xl font-heading font-semibold">{title}</h3>
        </div>
        
        <div className="p-5">
          {children}
        </div>
        
        <div className="p-5 bg-gray-50 rounded-b-lg flex justify-end gap-3">
          {cancelText && (
            <Button 
              variant="outline" 
              onClick={onClose}
            >
              {cancelText}
            </Button>
          )}
          
          {confirmText && onConfirm && (
            <Button 
              variant={variant === "warning" ? "danger" : "accent"} 
              onClick={onConfirm}
            >
              {confirmText}
            </Button>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
};

export default Modal;
